import { Suspense, lazy } from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import App from "@/App";
import CommonLayout from "@/components/layouts/common-layout";
import {
  AI_CHAT_PATHNAME,
  AI_HOME_PATHNAME,
  AI_SHARE_PATHNAME,
  ANNOUNCEMENT_PATHNAME,
  DASHBOARD_PATHNAME,
  JOB_PLANNING_PATHNAME,
  JOB_STATUS_PATHNAME,
  LOGIN_PATHNAME,
  MYPAGE_PATHNAME,
  NETWORK_PATHNAME,
  SEARCHED_PATHNAME,
  STATISTICS_PATHNAME,
} from "@/constants/pathname";
import PrivateRoute from "@/routes/private-route";
import PublicRoute from "@/routes/public-route";
import Spinner from "@/components/shared/spinner";
import ErrorPage from "@/pages/error-page";
import AiHomePage from "@/pages/ai-agent/ai-home-page";
import AiChatPage from "@/pages/ai-agent/ai-chat-page";
import AiSharePage from "@/pages/ai-agent/ai-share-page";

// 페이지 컴포넌트들을 lazy loading으로 변경
const NotFoundPage = lazy(() => import("@/pages/not-found-page"));
const LoginPage = lazy(() => import("@/pages/login/login-page"));
const DashboardPage = lazy(() => import("@/pages/home/dashboard-page"));
const MyPage = lazy(() => import("@/pages/home/my-page"));
const AnnouncementPage = lazy(() => import("@/pages/home/announcement-page"));
const AnnouncementDetailPage = lazy(
  () => import("@/pages/home/announcement-detail-page")
);
const JobStatusPage = lazy(() => import("@/pages/job/job-status-page"));
const JobPlanningPage = lazy(() => import("@/pages/job/job-planning-page"));
const SearchedPage = lazy(() => import("@/pages/searched/searched-page"));
const StatisticsPage = lazy(() => import("@/pages/statistics/statistics-page"));
const BioinformaticsPage = lazy(
  () => import("@/pages/bioinformatics/bioinformatics-page")
);
const ComponentsPage = lazy(() => import("@/pages/components-page"));

// Suspense로 감싼 페이지 컴포넌트를 생성하는 헬퍼 함수
const createSuspensePage = (
  Component: React.ComponentType,
  fallback: React.ReactNode
) => (
  <Suspense fallback={fallback}>
    <Component />
  </Suspense>
);

// CommonLayout + Suspense로 감싼 페이지 컴포넌트를 생성하는 헬퍼 함수
const createLayoutPage = (
  Component: React.ComponentType,
  fallback: React.ReactNode
) => (
  <CommonLayout>
    <Suspense fallback={fallback}>
      <Component />
    </Suspense>
  </CommonLayout>
);

const AppRouter = () => {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <App />,
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          element: <Navigate replace to={DASHBOARD_PATHNAME} />,
        },
        {
          element: <PublicRoute redirectTo={DASHBOARD_PATHNAME} />,
          children: [
            {
              path: LOGIN_PATHNAME,
              element: createSuspensePage(LoginPage, <Spinner />),
            },
          ],
        },
        {
          element: <PrivateRoute />,
          children: [
            {
              path: DASHBOARD_PATHNAME,
              element: createLayoutPage(DashboardPage, <Spinner />),
            },
            {
              path: MYPAGE_PATHNAME,
              element: createLayoutPage(MyPage, <Spinner />),
            },
            {
              path: ANNOUNCEMENT_PATHNAME,
              element: createLayoutPage(AnnouncementPage, <Spinner />),
            },
            {
              path: `${ANNOUNCEMENT_PATHNAME}/:id`,
              element: createLayoutPage(AnnouncementDetailPage, <Spinner />),
            },
            {
              path: JOB_STATUS_PATHNAME,
              element: createLayoutPage(JobStatusPage, <Spinner />),
            },
            {
              path: JOB_PLANNING_PATHNAME,
              element: createLayoutPage(JobPlanningPage, <Spinner />),
            },
            {
              path: SEARCHED_PATHNAME,
              element: createLayoutPage(SearchedPage, <Spinner />),
            },
            {
              path: STATISTICS_PATHNAME,
              element: createLayoutPage(StatisticsPage, <Spinner />),
            },
            {
              path: NETWORK_PATHNAME,
              element: createLayoutPage(BioinformaticsPage, <Spinner />),
            },
            {
              path: AI_HOME_PATHNAME,
              element: createLayoutPage(AiHomePage, <Spinner />),
            },
            {
              path: `${AI_CHAT_PATHNAME}/:id`,
              element: createLayoutPage(AiChatPage, <Spinner />),
            },
            {
              path: AI_CHAT_PATHNAME,
              element: createLayoutPage(AiChatPage, <Spinner />),
            },
            {
              path: `${AI_SHARE_PATHNAME}/:id`,
              element: createSuspensePage(AiSharePage, <Spinner />),
            },
          ],
        },
        {
          path: "/components",
          element: createSuspensePage(ComponentsPage, <Spinner />),
        },
        {
          path: "/*",
          element: createSuspensePage(NotFoundPage, <Spinner />),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

export default AppRouter;
